.result {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.controls {
  display: flex;
  justify-content: flex-start;
  background-color: var(--color-gray-dim);
  overflow-x: auto;
  scrollbar-width: thin;

  select {
    font-size: 1rem;
    border-radius: 0;
    background-color: var(--color-gray-dim);
  }
}

.control {
  display: flex;
  align-items: center;
  justify-content: center;
  border-right: 2px solid var(--color-gray-alt-dim);

  & > :hover {
    background-color: var(--color-gray-alt-dim);
    border-color: transparent;
  }
}

.version {
  font-family: "Ubuntu Mono", monospace;
  font-size: 0.9rem;
  flex-grow: 1;
  text-align: right;
  align-self: center;
  padding: 0 0.5rem;
}

.checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  gap: 0.5rem;
  border: 1px solid transparent;
  &:focus-within {
    outline: none;
    border-color: var(--section-color-alt, var(--color-blue-alt));
  }

  label {
    cursor: pointer;
  }

  input {
    accent-color: var(--color-blue);
    width: 1rem;
    height: 1rem;
    margin: 0;
    cursor: pointer;
  }
}

:global(.fullscreen) {
  visibility: hidden;
}

.output {
  position: relative;
  display: flex;
  flex: 1;
  // The compiled-code view scrolls internally (see #compiled) so the pane
  // itself stays put -- this keeps the absolute copy button pinned top-right.
  overflow: hidden;

  :global(.fullscreen) & {
    visibility: visible;
    position: fixed;
    inset: 0;
    z-index: 1000000;
  }

  iframe {
    flex: 1;
    border: none;
    /* TODO:
       Figure out why Chrome ignores the default iframe background color
       after the playground recovers from a syntax error, and remove this line.
       This is fine except `color-scheme: light dark` examples
    */
    background-color: white;
  }
}

.error {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  white-space: pre;
  padding: 1rem;
}

.stats {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  padding: 0 0.5rem;
  font-family: "Ubuntu Mono", monospace;
  overflow-x: auto;
  scrollbar-width: thin;
}

.statEquals {
  padding: 0.2em;
}

.previewControl {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  padding: 0;
  border: none;
  background: none;
  flex-shrink: 0;

  &:hover {
    background-color: var(--color-gray-alt-dim);
  }

  svg {
    height: 1lh;
    fill: var(--color-blue);
  }
}

// Floating action button pinned to the top-right of the output pane;
// matches the editor pane's format button (see editor.styles).
.action {
  position: absolute;
  top: 0.5rem;
  right: 0.75rem;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.4rem 0.5rem;
  border: none;
  border-radius: 0.4rem;
  background-color: var(--color-gray-dim);
  cursor: pointer;
  opacity: 0.65;
  transition: opacity 0.15s ease;

  &:hover,
  &:focus-visible {
    opacity: 1;
    background-color: var(--color-gray-alt-dim);
  }

  svg {
    height: 1lh;
    fill: var(--color-blue);
  }
}

.shareControl svg {
  width: 1.25lh;
}

.copied {
  animation: copied 1.5s ease;
}

@keyframes copied {
  0% {
    transform: scale(0.6);
    opacity: 0.4;
  }
  20% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.loading {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-black);
  font-family: "Ubuntu Mono", monospace;
  font-size: 1.25rem;
  animation: fade-in 0.3s ease 1s both;

  span {
    opacity: 0.35;
    animation: pulse 2.4s ease-in-out infinite;
  }
}

@keyframes fade-in {
  from {
    opacity: 0;
  }
}

@keyframes pulse {
  50% {
    opacity: 1;
  }
}

.spin {
  animation: spin 0.25s ease-out;
}

@keyframes spin {
  to {
    transform: rotate(1turn);
  }
}

#compiled {
  display: flex;
  flex: 1;
  max-width: 100%;
  overflow: auto;

  > :global(.shiki) {
    flex: 1;
    counter-reset: line-number;
    padding: 1rem 0;
    padding-right: 0.25rem;
    padding-left: 0.5rem;

    :global(.line) {
      position: relative;
      counter-increment: line-number;
      &::before {
        display: inline-flex;
        content: counter(line-number);
        padding-right: 1.625rem;
        width: 3ch;
        justify-content: right;
        color: #858585;
        user-select: none;
        font-feature-settings: "tnum";
        font-variant-numeric: tabular-nums;
      }
    }
  }
}

.hide {
  display: none;
}

.console {
  display: flex;
  flex-direction: column;
  border-top: 2px solid var(--color-gray-alt-dim);
  background-color: var(--color-gray-dim);

  // In fullscreen, collapse to a minimal control cluster in the corner --
  // no console toggle or log output (see below).
  :global(.fullscreen) & {
    visibility: visible;
    position: fixed;
    bottom: 0;
    right: 0;
    z-index: 1000001;
    border-top: none;
    border-top-left-radius: 0.5rem;
  }
}

.consoleBar {
  display: flex;
  align-items: stretch;
  min-height: 2rem;
}

.consoleToggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  border: none;
  background: none;
  cursor: pointer;
  font: inherit;
  color: inherit;

  svg {
    height: 0.75lh;
    fill: var(--color-blue);
  }

  &:hover {
    background-color: var(--color-gray-alt-dim);
  }

  :global(.fullscreen) & {
    display: none;
  }
}

.consoleCount {
  background-color: var(--color-gray-alt-dim);
  border-radius: 1rem;
  padding: 0 0.5rem;
  font-size: 0.8rem;
}

.consoleClear {
  display: flex;
  align-items: center;
  padding: 0 0.75rem;
  border: none;
  background: none;
  cursor: pointer;

  svg {
    height: 1lh;
    fill: var(--color-blue);
  }

  &:hover {
    background-color: var(--color-gray-alt-dim);
  }

  :global(.fullscreen) & {
    display: none;
  }
}

.consoleBody {
  height: 12rem;
  overflow-y: auto;
  border-top: 1px solid var(--color-gray-alt-dim);
  font-family: "Ubuntu Mono", monospace;
  font-size: 0.85rem;

  :global(.fullscreen) & {
    display: none;
  }
}

.consoleEmpty {
  padding: 0.5rem;
  opacity: 0.6;
}

.logEntry {
  display: flex;
  gap: 0.5rem;
  align-items: baseline;
  padding: 0.15rem 0.5rem;
  white-space: pre-wrap;
  word-break: break-word;

  & + & {
    border-top: 1px solid var(--color-gray-alt-dim);
  }

  &[data-method="error"] {
    color: var(--color-red);
  }

  &[data-method="warn"] {
    color: var(--color-yellow);
  }
}

.logNs {
  flex-shrink: 0;
  text-transform: uppercase;
  font-size: 0.7rem;
  opacity: 0.8;

  &[data-ns="server"] {
    color: var(--color-blue);
  }

  &[data-ns="client"] {
    color: var(--color-red);
  }
}

.logText {
  flex-grow: 1;
}

.markoLogo {
  display: none;
  position: absolute;
  left: 0;
  bottom: 0;
  padding: 0.5rem;
  height: 2.5rem;
  width: 3.5rem;
  background-clip: content-box;
  background-repeat: no-repeat;
  background-position: 0.5rem 0.2rem;
  background-size: auto 2rem;
  background-image: url("/fav.svg");
  filter: brightness(0%) invert(30%);

  &:hover {
    transition: filter 0.2s ease;
    filter: none;
  }

  :global(.fullscreen) & {
    display: block;
  }
}
